import _ from 'underscore';

import { DatePicker } from '@bingads-webui/date-picker';
import { validateDateRange } from '@bingads-webui-universal/primitive-utilities';
import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';

import template from './custom-date-range-selection-view-template.pug';

const { updateView, ensureChildView } = helpers;

/**
 * This panel allows selection of a custom date range
 * @param {Date} startDate - the start date of the date range
 * @param {Date} endDate - the end date of the date range
 * @param {Date} minDate - the minimum date of the date range
 * @param {Date} maxDate - the maximum date of the date range
 * @extends {View}
 */
export class CustomDateRangeSelectionPanel extends DeterministicView {
  initialize(options) {
    this.options = options;
    updateView(this, {
      startDateErrors: [],
      endDateErrors: [],
      generalErrors: [],
    });
    updateView(this, { startDate: this.options.startDate });
    updateView(this, { endDate: this.options.endDate });
    this.showDatePickers();
  }

  showDatePickers() {
    const { minDate, maxDate } = this.options;

    ensureChildView(this, 'startDatePicker', DatePicker, {
      selectedDate: this.model.get('startDate'),
      minDate,
      maxDate,
    }, {
      rendered() {
        const finalStartDate = new Date(Math.min(maxDate, this.model.get('endDate')));
        this.getChild('startDatePicker').datePicker.max(finalStartDate);
      },
      dateChanged: (startDate) => {
        updateView(this, { startDate });
        this.getChild('endDatePicker').datePicker.min(startDate);
        this.validate();
      },
    });
    ensureChildView(this, 'endDatePicker', DatePicker, {
      selectedDate: this.model.get('endDate'),
      minDate,
      maxDate,
    }, {
      rendered() {
        const finalEndDate = new Date(Math.max(minDate, this.model.get('startDate')));
        this.getChild('endDatePicker').datePicker.min(finalEndDate);
      },
      dateChanged: (endDate) => {
        updateView(this, { endDate });
        this.getChild('startDatePicker').datePicker.max(endDate);
        this.validate();
      },
    });
  }

  validate() {
    const { minDate, maxDate, i18n } = this.options;
    const { startDate, endDate } = this.model.attributes;

    const generalErrors = (endDate < startDate) ? [i18n.getString('StartDate_GreaterThan_EndDate')] : [];
    const startDateErrors = _.map(validateDateRange(minDate, maxDate, this.model.get('startDate')), key => i18n.getString(key));
    const endDateErrors = _.map(validateDateRange(minDate, maxDate, this.model.get('endDate')), key => i18n.getString(key));

    updateView(this, { generalErrors, startDateErrors, endDateErrors });
    return _.union(generalErrors, startDateErrors, endDateErrors);
  }

  getSelection() {
    const errors = this.validate();
    const { startDate, endDate } = this.getState();
    return {
      start: startDate,
      end: endDate,
      invalid: errors.length > 0,
    };
  }

  get template() {
    return template;
  }
}
